import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { vehicleSelections, VehicleSelection } from '@ltvco/refresh-lib/v1';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  Control,
  UseFormTrigger,
  useQuery,
} from '@ltvco/refresh-lib/vendors';
import React, { useState, useEffect } from 'react';

import { composeHOC } from '../hoc';
import { withErrorHandling } from '../hoc/withErrorHandling';
import { SelectInput } from '../SelectInput';

import {
  StyledSelectInputContainer,
  VehicleInputsErrorTextPositionWrapper,
  VehicleSearchFieldContainer,
  YMMSelectInputWrapper,
} from './components';
import {
  getYears,
  getMakes,
  getModels,
  getVehicleTrims,
  VehicleFormData,
} from './utils';

const EnhancedSelectInput = composeHOC(withErrorHandling)(SelectInput);

interface YMMSearchInputProps {
  register: UseFormRegister<VehicleFormData>;
  errors: FieldErrors<VehicleFormData>;
  clearErrors: UseFormClearErrors<VehicleFormData>;
  setValue: UseFormSetValue<VehicleFormData>;
  control: Control<VehicleFormData>;
  trigger: UseFormTrigger<VehicleFormData>;
  yearValue: string;
  makeValue: string;
  modelValue: string;
  trimValue: string;
  onSubmit?: () => void;
  isMobile?: boolean;
}

type YMMPossibleFieldNames = 'year' | 'make' | 'model' | 'trim';

export const YMMSearchInput = React.memo(
  ({
    errors,
    clearErrors,
    setValue,
    control,
    yearValue,
    makeValue,
    modelValue,
    trimValue,
    isMobile = false,
  }: YMMSearchInputProps) => {
    const years = getYears();
    const [customYearValue, setCustomYearValue] = useState(yearValue);
    const [customMakeValue, setCustomMakeValue] = useState(makeValue);
    const [customModelValue, setCustomModelValue] = useState(modelValue);
    const [customTrimValue, setCustomTrimValue] = useState(trimValue);
    const [makes, setMakes] = useState<string[]>([]);
    const [models, setModels] = useState<string[]>([]);
    const [trims, setTrims] = useState<string[]>([]);
    const [makesObj, setMakesObj] = useState<any[]>([]);
    const [selectedMake, setSelectedMake] = useState(makeValue);
    const [selectedModel, setSelectedModel] = useState(modelValue);
    const [prioritizedError, setPrioritizedError] = useState<{
      field: string;
      message: string;
    } | null>(null);

    const { data } = useQuery<VehicleSelection>({
      queryKey: ['vehicleSelections'],
      queryFn: () => vehicleSelections(),
    });

    // Determine the most important error to display
    useEffect(() => {
      if (errors) {
        if (errors.year) {
          setPrioritizedError({
            field: 'year',
            message: errors.year.message || 'Year is required',
          });
        } else if (errors.make) {
          setPrioritizedError({
            field: 'make',
            message: errors.make.message || 'Make is required',
          });
        } else if (errors.model) {
          setPrioritizedError({
            field: 'model',
            message: errors.model.message || 'Model is required',
          });
        } else if (errors.trim) {
          setPrioritizedError({
            field: 'trim',
            message: errors.trim.message || 'Trim is required',
          });
        } else {
          setPrioritizedError(null);
        }
      } else {
        setPrioritizedError(null);
      }
    }, [errors]);

    useEffect(() => {
      if (data?.selections) {
        const { selections } = data;
        setMakesObj(selections.makes);
        setMakes(getMakes(selections.makes));
      }
    }, [data]);

    // Sync external prop changes (e.g. from form reset with pre-filled URL values)
    useEffect(() => {
      setCustomYearValue(yearValue);
    }, [yearValue]);

    useEffect(() => {
      setCustomMakeValue(makeValue);
      setSelectedMake(makeValue);
    }, [makeValue]);

    useEffect(() => {
      setCustomModelValue(modelValue);
      setSelectedModel(modelValue);
    }, [modelValue]);

    useEffect(() => {
      setCustomTrimValue(trimValue);
    }, [trimValue]);

    useEffect(() => {
      setModels(getModels(selectedMake, makesObj));
      clearErrors('model');
    }, [selectedMake, clearErrors, makesObj]);

    useEffect(() => {
      setTrims(getVehicleTrims(selectedMake, selectedModel, makesObj));
    }, [selectedModel, makesObj, selectedMake]);

    const clearAllErrors = () => {
      clearErrors('year');
      clearErrors('make');
      clearErrors('model');
      clearErrors('trim');
      setPrioritizedError(null);
    };

    const handleOptionChange = (
      value: string,
      field: YMMPossibleFieldNames
    ) => {
      setValue(field, value);
      clearErrors(field);

      if (field === 'year') {
        setCustomYearValue(value);
        clearAllErrors();
      }

      if (field === 'make') {
        setCustomMakeValue(value);
        setSelectedMake(value);

        setCustomModelValue('');
        setValue('model', '');
        setCustomTrimValue('');
        setValue('trim', '');
      }

      if (field === 'model') {
        setCustomModelValue(value);
        setSelectedModel(value);

        setCustomTrimValue('');
        setValue('trim', '');
      }

      if (field === 'trim') {
        setCustomTrimValue(value);
      }
    };

    const yearsOptions = years.map((year) => ({ value: year, label: year }));
    const makesOptions = makes.map((make) => ({ value: make, label: make }));
    const modelsOptions = models.map((model) => ({
      value: model,
      label: model,
    }));
    const trimsOptions = trims.map((trim) => ({ value: trim, label: trim }));

    const isModelDisabled = customMakeValue === '';
    const isTrimDisabled = customMakeValue === '' || customModelValue === '';

    const renderMobileInputs = () => {
      const errorClassName = Object.keys(errors).length > 0 ? 'error' : '';

      return (
        <Grid container spacing={2} gap={1} flexDirection={'column'}>
          <Grid size={{ xs: 12 }}>
            <VehicleInputsErrorTextPositionWrapper>
              <VehicleSearchFieldContainer
                className={
                  prioritizedError?.field === 'year' ? errorClassName : ''
                }
              >
                <StyledSelectInputContainer>
                  <EnhancedSelectInput
                    options={[{ value: '', label: 'Year' }, ...yearsOptions]}
                    value={customYearValue}
                    onChange={(value: string) =>
                      handleOptionChange(value, 'year')
                    }
                    name="year"
                    control={control}
                    defaultOptionValue={''}
                    error={
                      prioritizedError?.field === 'year'
                        ? { message: prioritizedError.message }
                        : undefined
                    }
                  />
                </StyledSelectInputContainer>
              </VehicleSearchFieldContainer>
            </VehicleInputsErrorTextPositionWrapper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <VehicleInputsErrorTextPositionWrapper>
              <VehicleSearchFieldContainer
                className={
                  prioritizedError?.field === 'make' ? errorClassName : ''
                }
              >
                <StyledSelectInputContainer>
                  <EnhancedSelectInput
                    options={[{ value: '', label: 'Make' }, ...makesOptions]}
                    value={customMakeValue}
                    onChange={(value: string) =>
                      handleOptionChange(value, 'make')
                    }
                    name="make"
                    control={control}
                    defaultOptionValue={''}
                    error={
                      prioritizedError?.field === 'make'
                        ? { message: prioritizedError.message }
                        : undefined
                    }
                  />
                </StyledSelectInputContainer>
              </VehicleSearchFieldContainer>
            </VehicleInputsErrorTextPositionWrapper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <VehicleInputsErrorTextPositionWrapper>
              <VehicleSearchFieldContainer
                className={
                  prioritizedError?.field === 'model' ? errorClassName : ''
                }
              >
                <StyledSelectInputContainer>
                  <EnhancedSelectInput
                    options={[{ value: '', label: 'Model' }, ...modelsOptions]}
                    value={customModelValue}
                    onChange={(value: string) =>
                      handleOptionChange(value, 'model')
                    }
                    name="model"
                    control={control}
                    defaultOptionValue={''}
                    disabled={isModelDisabled}
                    error={
                      prioritizedError?.field === 'model'
                        ? { message: prioritizedError.message }
                        : undefined
                    }
                  />
                </StyledSelectInputContainer>
              </VehicleSearchFieldContainer>
            </VehicleInputsErrorTextPositionWrapper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <VehicleInputsErrorTextPositionWrapper>
              <VehicleSearchFieldContainer
                className={
                  prioritizedError?.field === 'trim' ? errorClassName : ''
                }
              >
                <StyledSelectInputContainer>
                  <EnhancedSelectInput
                    options={[{ value: '', label: 'Trim' }, ...trimsOptions]}
                    value={customTrimValue}
                    onChange={(value: string) =>
                      handleOptionChange(value, 'trim')
                    }
                    name="trim"
                    control={control}
                    defaultOptionValue={''}
                    disabled={isTrimDisabled}
                    error={
                      prioritizedError?.field === 'trim'
                        ? { message: prioritizedError.message }
                        : undefined
                    }
                  />
                </StyledSelectInputContainer>
              </VehicleSearchFieldContainer>
            </VehicleInputsErrorTextPositionWrapper>
          </Grid>
        </Grid>
      );
    };

    const renderDesktopInputs = () => {
      return (
        <VehicleInputsErrorTextPositionWrapper>
          <Grid
            container
            spacing={1}
            gap={0}
            flexDirection={'row'}
            className="ymm-search-container"
          >
            <YMMSelectInputWrapper size={{ xs: 12, sm: 3 }}>
              <StyledSelectInputContainer>
                <EnhancedSelectInput
                  options={[{ value: '', label: 'Year' }, ...yearsOptions]}
                  value={customYearValue}
                  onChange={(value: string) =>
                    handleOptionChange(value, 'year')
                  }
                  name="year"
                  control={control}
                  defaultOptionValue={''}
                  error={
                    prioritizedError?.field === 'year'
                      ? { message: prioritizedError.message }
                      : undefined
                  }
                />
              </StyledSelectInputContainer>
            </YMMSelectInputWrapper>

            <YMMSelectInputWrapper size={{ xs: 12, sm: 3 }}>
              <StyledSelectInputContainer>
                <EnhancedSelectInput
                  options={[{ value: '', label: 'Make' }, ...makesOptions]}
                  value={customMakeValue}
                  onChange={(value: string) =>
                    handleOptionChange(value, 'make')
                  }
                  name="make"
                  control={control}
                  defaultOptionValue={''}
                  error={
                    prioritizedError?.field === 'make'
                      ? { message: prioritizedError.message }
                      : undefined
                  }
                />
              </StyledSelectInputContainer>
            </YMMSelectInputWrapper>

            <YMMSelectInputWrapper size={{ xs: 12, sm: 3 }}>
              <StyledSelectInputContainer>
                <EnhancedSelectInput
                  options={[{ value: '', label: 'Model' }, ...modelsOptions]}
                  value={customModelValue}
                  onChange={(value: string) =>
                    handleOptionChange(value, 'model')
                  }
                  name="model"
                  control={control}
                  defaultOptionValue={''}
                  disabled={isModelDisabled}
                  error={
                    prioritizedError?.field === 'model'
                      ? { message: prioritizedError.message }
                      : undefined
                  }
                />
              </StyledSelectInputContainer>
            </YMMSelectInputWrapper>

            <YMMSelectInputWrapper size={{ xs: 12, sm: 3 }}>
              <StyledSelectInputContainer>
                <EnhancedSelectInput
                  options={[{ value: '', label: 'Trim' }, ...trimsOptions]}
                  value={customTrimValue}
                  onChange={(value: string) =>
                    handleOptionChange(value, 'trim')
                  }
                  name="trim"
                  control={control}
                  defaultOptionValue={''}
                  disabled={isTrimDisabled}
                  error={
                    prioritizedError?.field === 'trim'
                      ? { message: prioritizedError.message }
                      : undefined
                  }
                />
              </StyledSelectInputContainer>
            </YMMSelectInputWrapper>
          </Grid>
        </VehicleInputsErrorTextPositionWrapper>
      );
    };

    return (
      <Box sx={{ width: '100%' }}>
        {isMobile ? renderMobileInputs() : renderDesktopInputs()}
      </Box>
    );
  }
);
